import { confirmResetPassword, resetPassword } from "aws-amplify/auth";
import { useState } from "react";
import handleEmailValidation from "../utils/emailValidator";
import PasswordValidator from "password-validator";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PasswordCheckComponent } from "./PasswordCheckComponent";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { PasswordInput } from "@/components/ui/password-input";
import { useForm } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { REGEXP_ONLY_DIGITS } from "input-otp";

const ForgotPassword = () => {
  //Define zod formschema
  const formSchema = z.object({
    email: z
      .string()
      .min(1, "Please provide your email")
      .refine((val) => handleEmailValidation(val), {
        message: "Please provide correct email",
      }),
    code: z.string().min(6, "Six digit reset code is required"),
    password: z.string(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "Dummy",
      code: "123456",
    },
    mode: "onChange",
  });

  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);

  const [isLengthValid, setIsLengthValid] = useState(false);
  const [isUpperCase, setIsUpperCase] = useState(false);
  const [isLowerCase, setIsLowerCase] = useState(false);
  const [hasDigits, setHasDigits] = useState(false);
  const [hasSymbols, setHasSymbols] = useState(false);
  const [hasSpaces, setHasSpaces] = useState(false);

  const isValid = form.formState.isValid;
  const [isPasswordValid, setIsPasswordValid] = useState(true);

  // Password validation schema
  const passwordSchema = new PasswordValidator()
    .is()
    .min(8) // Minimum length 8
    .is()
    .max(20) // Maximum length 20
    .has()
    .uppercase() // Must have uppercase letters
    .has()
    .lowercase() // Must have lowercase letters
    .has()
    .digits() // Must have digits
    .has()
    .symbols() // Must have symbols
    .has()
    .not()
    .spaces(); // Should not have spaces

  //Validate password meets the password schema requirements
  const getPasswordValidation = (password: string) => {
    const failedRules = passwordSchema.validate(password, {
      list: true,
    }) as string[];
    setIsLengthValid(
      !failedRules.includes("min") && !failedRules.includes("max")
        ? true
        : false,
    );
    setIsUpperCase(!failedRules.includes("uppercase") ? true : false);
    setIsLowerCase(!failedRules.includes("lowercase") ? true : false);
    setHasDigits(!failedRules.includes("digits") ? true : false);
    setHasSymbols(!failedRules.includes("symbols") ? true : false);
    setHasSpaces(!failedRules.includes("spaces") ? true : false);
    setIsPasswordValid(
      isLengthValid &&
        isUpperCase &&
        isLowerCase &&
        hasDigits &&
        hasSymbols &&
        hasSpaces,
    );
  };

  //Send request to aws amplify to get reset code
  const submitGetVerificationCode = async (
    values: z.infer<typeof formSchema>,
  ) => {
    resetPassword({ username: values.email.toLowerCase() })
      .then(() => {
        setSuccess(true);
        form.setValue("code", "");
        form.setValue("password", "");
        toast.success("Reset Code sent successfully");
        setIsPasswordValid(false);
      })
      .catch((error) => {
        console.log(error);
        if (
          error.message ===
          "Attempt limit exceeded, please try after some time."
        ) {
          toast.error(error.message);
        } else if (
          error.message === "Username/client id combination not found."
        ) {
          toast.error("Email not found, please check if email is correct.");
        }
      });
  };

  //Send request to aws amplify to reset password
  const submitResetPassword = (values: z.infer<typeof formSchema>) => {
    confirmResetPassword({
      username: values.email.toLowerCase(),
      confirmationCode: values.code.replace(/\s/g, ""),
      newPassword: values.password,
    })
      .then(() => {
        toast.success(
          "Password reset successful. Please sign in with your new password.",
        );
        setSuccess(true);
        navigate("/sign-in");
      })
      .catch((error) => {
        console.log(error);
        if (
          error.message ===
          "Invalid verification code provided, please try again."
        ) {
          form.setValue("code", "");
          form.setValue("password", "");
          toast.error("Invalid reset code, please try again");
        }
        if (
          error.message ===
          "Invalid code provided, please request a code again."
        ) {
          toast.error(
            "Reset Code expired, please request the reset code again.",
          );
        }
      });
  };

  // Note: numeric filtering is handled by the OTP component; no custom key filter needed

  const getVerificationCodeView = () => (
    <FormField
      control={form.control}
      name="email"
      render={({ field }) => (
        <FormItem className="text-lg">
          <FormLabel className="text-lg block mb-2">Email</FormLabel>
          <FormControl>
            <Input
              className="w-full px-3 py-2 text-lg h-12 border rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Email"
              type="email"
              {...field}
            />
          </FormControl>
          <FormMessage className="text-sm text-red-500 mt-1" />
        </FormItem>
      )}
    />
  );

  const submitCodeView = () => (
    <>
      <FormField
        control={form.control}
        name="code"
        render={({ field }) => (
          <FormItem className="text-lg">
            <FormLabel className="text-lg">Code:</FormLabel>
            <FormControl className="w-full">
              <InputOTP
                maxLength={6}
                //Adding regex to only allow digits
                pattern={REGEXP_ONLY_DIGITS}
                value={field.value ?? ""}
                onChange={field.onChange}
              >
                <InputOTPGroup className="w-full">
                  <InputOTPSlot index={0} className="w-1/6" />
                  <InputOTPSlot index={1} className="w-1/6" />
                  <InputOTPSlot index={2} className="w-1/6" />
                  <InputOTPSlot index={3} className="w-1/6" />
                  <InputOTPSlot index={4} className="w-1/6" />
                  <InputOTPSlot index={5} className="w-1/6" />
                </InputOTPGroup>
              </InputOTP>
            </FormControl>

            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="password"
        render={({ field }) => (
          <FormItem className="text-lg">
            <FormLabel className="text-lg">New Password</FormLabel>
            <FormControl>
              <PasswordInput
                className="text-lg h-12"
                placeholder="Password"
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  getPasswordValidation(e.currentTarget.value);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <PasswordCheckComponent
        isLengthValid={isLengthValid}
        isLowerCase={isLowerCase}
        hasDigits={hasDigits}
        hasSymbols={hasSymbols}
        hasSpaces={hasSpaces}
        isUpperCase={isUpperCase}
      />
    </>
  );

  return (
    <div className="flex flex-col w-full min-h-screen overflow-y-auto">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(
            //Submit form based on different requirement
            success ? submitResetPassword : submitGetVerificationCode,
          )}
          className="space-y-8 max-w-full mx-4 sm:mx-20 md:mx-24 lg:mx-80 xl:mx-96 2xl:mx-[30rem] px-4 py-8 sm:py-16"
        >
          <div className="bg-white shadow-2xl rounded-lg w-full p-10">
            <fieldset className="p-5 space-y-4">
              <h1 className="text-2xl font-bold">Reset your password</h1>
              {success ? (
                <p className="text-gray-500 mb-4">
                  Reset code has been sent to the provided email.
                </p>
              ) : (
                <p className="text-gray-500 mb-4">
                  Please enter your email. The reset code will be sent to this
                  address.
                </p>
              )}
              {/* Load form based on different requirement */}
              {success ? submitCodeView() : getVerificationCodeView()}
              <Button
                type="submit"
                className="w-full shadow-xl bg-red-700 hover:bg-red-800 hover:cursor-pointer text-white font-bold py-3 px-4 rounded-lg mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!isValid || !isPasswordValid}
              >
                {success ? "Reset password" : "Send code"}
              </Button>
              <Label className="text-left text-lg mt-2">
                <NavLink to="/sign-in" className="text-red-500 hover:underline">
                  Back to Sign In
                </NavLink>
              </Label>
            </fieldset>
          </div>
        </form>
      </Form>
    </div>
  );
};

export { ForgotPassword };
